import { useAuth } from "@clerk/react";
import { ClientOnly, useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { AuthnFormDialog } from "@/web/auth/AuthnFormDialog";
import { Button } from "@/web/components/primitives/button";
import { cn } from "@/web/lib/styling";
import { NominateButton } from "@/web/routes/-nomination/NominateButton";

/**
 * Tile skin applied to the ghost `Button` primitive (and to NominateButton's
 * trigger). The Button wraps children in an inline-flex span; the
 * `[&>span:first-child]` rules turn it into the tile's vertical stack.
 */
const TILE = cn(
  "group h-full min-h-[245px] w-full flex-col items-stretch justify-start rounded-[2px] bg-white p-[clamp(24px,2.4vw,36px)]",
  "border-[#d4d7de] text-left text-[16px] leading-[1.5] font-normal whitespace-normal text-[#050f23] [--button-border-width:1px]",
  "transition-[background-color,color,border-color,translate] duration-250 hover:-translate-y-[3px] hover:border-[#2f2f8c]",
  "[&>span:first-child]:flex [&>span:first-child]:w-full [&>span:first-child]:flex-col [&>span:first-child]:items-stretch [&>span:first-child]:gap-0",
  "max-[760px]:min-h-[208px] max-[760px]:p-[24px]",
);

function TileContents({ number, title, note }: { number: string; title: string; note: string }) {
  return (
    <>
      <span className="mb-[36px] text-[11px] leading-[1.3] tracking-[0.07em] max-[900px]:mb-[28px]">
        {number}
      </span>
      <span className="flex min-h-[2.3em] items-center justify-between gap-[20px] text-[clamp(25px,2.1vw,32px)] leading-[1.15] tracking-[-0.04em] max-[900px]:min-h-0 max-[760px]:gap-[12px]">
        {title}
        <span
          aria-hidden="true"
          className="flex size-[43px] shrink-0 items-center justify-center rounded-[2px] bg-[#ebcd46] text-[23px] text-[#050f23] group-hover:bg-[#2f2f8c] group-hover:text-white max-[760px]:size-[38px] max-[760px]:text-[21px]"
        >
          ↗
        </span>
      </span>
      <span className="mt-[14px] text-[14px] leading-[1.5] max-[760px]:text-[13px]">{note}</span>
    </>
  );
}

/** The business vs. investor onboarding fork. */
const ONBOARDING_PATH = "/onboarding";

/**
 * A path tile that starts onboarding: signed out, it opens the same sign-in
 * dialog as the navbar's "Sign in", which then lands on `/onboarding` (rather
 * than the homepage's usual `/profile`); signed in, it goes straight there.
 *
 * Client-only because `useAuth` needs `<ClerkProvider>`, which only mounts on
 * the client (see __root.tsx); the SSR fallback is the same tile, inert.
 */
function OnboardingTile({ testId, children }: { testId: string; children: ReactNode }) {
  const tile = (
    <Button variant="ghost" className={TILE} data-testid={testId}>
      {children}
    </Button>
  );
  return (
    <ClientOnly fallback={tile}>
      <OnboardingTileInner testId={testId}>{children}</OnboardingTileInner>
    </ClientOnly>
  );
}

function OnboardingTileInner({ testId, children }: { testId: string; children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const signedIn = isLoaded && isSignedIn;

  const tile = (
    <Button
      variant="ghost"
      className={TILE}
      data-testid={testId}
      onClick={signedIn ? () => void navigate({ to: ONBOARDING_PATH }) : undefined}
    >
      {children}
    </Button>
  );
  // Until Clerk resolves, render the tile without an action rather than guess.
  if (!isLoaded || signedIn) return tile;
  return (
    <AuthnFormDialog
      trigger={tile}
      title="Welcome"
      description="Continue with Google or email"
      redirectTo={ONBOARDING_PATH}
    />
  );
}

/** "Three ways to take part": building owners, business owners, community. */
export function PathCards() {
  return (
    <section
      id="join"
      data-nav-theme="white"
      className="border-t border-[#d4d7de] px-(--gutter) pt-[110px] pb-[80px] max-[760px]:pt-[65px] max-[760px]:pb-[50px]"
    >
      <div className="grid grid-cols-3 gap-[22px] max-[900px]:grid-cols-1 max-[760px]:gap-[16px]">
        {/* TODO(homepage-port): building owners need their own (landlord) onboarding;
            until it exists this lands on the generic business/investor fork. */}
        <OnboardingTile testId="home-path-building">
          <TileContents
            number="01 / BUILDING OWNERS"
            title="I own a building"
            note="Ready for what’s next? Explore a sale or a way to stay invested."
          />
        </OnboardingTile>
        <OnboardingTile testId="home-path-business">
          <TileContents
            number="02 / BUSINESS OWNERS"
            title="I own a business"
            note="You built the business. Let’s talk about a stake in the building."
          />
        </OnboardingTile>
        <NominateButton variant="ghost" className={TILE} data-testid="home-path-nominate">
          <TileContents
            number="03 / COMMUNITY INVESTORS"
            title="I believe in my town"
            note="Nominate a business you’d like to see thrive in your community."
          />
        </NominateButton>
      </div>
    </section>
  );
}
